/**
 * EventSeriesDetailPage - Series detail with upcoming/past instances (#1208)
 *
 * Route: /clubs/:slug/series/:seriesId
 *
 * Sections:
 * 1. Header - title, pattern, location
 * 2. Upcoming instances (next 10)
 * 3. Past instances (collapsed by default)
 * 4. Actions via kebab menu
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../hooks/useClubs';
import { useSeriesDetail, useDeleteSeries } from '../hooks/useEvents';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Button, EmptyState, Skeleton } from '../components';
import { Repeat, MapPin, Calendar, Users, ChevronDown, ChevronUp, Edit, XCircle, Search } from 'lucide-react';
import { EventDateTime } from '../components/ui/DateTimeDisplay';
import { SeriesDeleteModal } from '../components/SeriesDeleteModal';
import { toast } from 'sonner';

export default function EventSeriesDetailPage() {
  const { slug, seriesId } = useParams<{ slug: string; seriesId: string }>();
  const navigate = useNavigate();
  useAuth();

  const [showPast, setShowPast] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch club details
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');

  // Fetch series detail
  const {
    data: seriesData,
    isLoading: seriesLoading,
    error: seriesError,
  } = useSeriesDetail(clubData?.club.id || '', seriesId || '');

  // Delete mutation
  const deleteSeries = useDeleteSeries(clubData?.club.id || '');

  const loading = clubLoading || seriesLoading;
  const error = clubError || seriesError;
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeader = membership?.role === 'leader' || membership?.is_owner;

  // Handle delete
  const handleDelete = (scope: 'next' | 'all_future') => {
    if (!seriesId) return;

    deleteSeries.mutate(
      { seriesId, scope },
      {
        onSuccess: (response) => {
          toast.success(`Deleted ${response.deleted_count} event${response.deleted_count > 1 ? 's' : ''}`);
          setShowDeleteModal(false);
          navigate(`/clubs/${slug}/events/manage`);
        },
      }
    );
  };

  // Handle edit (navigate to edit page)
  const handleEdit = () => {
    if (!slug || !seriesId) return;
    navigate(`/clubs/${slug}/series/${seriesId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Series" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-4">
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
              <Skeleton className="h-6 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !club || !slug || !seriesData) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Series" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<Search className="w-12 h-12" />}
              title="Series Not Found"
              message="This series doesn't exist or you don't have access."
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  const { series, upcoming_instances, past_instances } = seriesData;

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title={series.title}
        subtitle="Event Series"
        onBack={() => navigate(`/clubs/${slug}/events/manage`)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Header Card */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Repeat className="w-5 h-5 text-accent" />
              <h1 className="text-lg font-semibold text-text-primary">{series.title}</h1>
            </div>

            <div className="space-y-2 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-text-muted" />
                <span>{series.recurrence_pattern}</span>
              </div>

              {series.location_name && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-text-muted" />
                  <span>{series.location_name}</span>
                </div>
              )}

              {series.description && (
                <p className="text-text-muted mt-3 whitespace-pre-wrap">{series.description}</p>
              )}
            </div>

            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-oro-border/50">
              <span className="text-sm text-accent">
                {series.upcoming_count} upcoming
              </span>
              <span className="text-sm text-text-muted">
                {series.past_count} past
              </span>
            </div>
          </div>

          {/* Upcoming Instances */}
          <div>
            <h2 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">
              Upcoming Events
            </h2>

            {upcoming_instances.length > 0 ? (
              <div className="space-y-2">
                {upcoming_instances.map((instance) => (
                  <button
                    key={instance.id}
                    onClick={() => navigate(`/event/${instance.short_code}`)}
                    className="w-full bg-oro-surface border border-oro-border rounded-lg p-3 text-left hover:bg-oro-surface-alt transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-text-muted" />
                        <EventDateTime timestamp={instance.starts_at} />
                      </div>
                      <div className="flex items-center gap-1 text-sm text-accent">
                        <Users className="w-3.5 h-3.5" />
                        <span>{instance.rsvp_count}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-oro-surface border border-oro-border rounded-lg p-4 text-center text-text-muted">
                No upcoming events in this series
              </div>
            )}
          </div>

          {/* Past Instances (collapsible) */}
          {past_instances.length > 0 && (
            <div>
              <button
                onClick={() => setShowPast(!showPast)}
                className="flex items-center gap-2 text-sm font-medium text-text-muted uppercase tracking-wider mb-3 hover:text-text-secondary transition-colors"
              >
                {showPast ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                Past Events ({past_instances.length})
              </button>

              {showPast && (
                <div className="space-y-2">
                  {past_instances.map((instance) => (
                    <button
                      key={instance.id}
                      onClick={() => navigate(`/event/${instance.short_code}`)}
                      className="w-full bg-oro-surface/50 border border-oro-border/50 rounded-lg p-3 text-left hover:bg-oro-surface-alt transition-colors opacity-70"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-text-muted" />
                          <EventDateTime timestamp={instance.starts_at} className="text-text-muted" />
                        </div>
                        <div className="flex items-center gap-1 text-sm text-text-muted">
                          <Users className="w-3.5 h-3.5" />
                          <span>{instance.rsvp_count}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Leader Actions */}
          {isLeader && (
            <div className="pt-4 border-t border-oro-border/50 space-y-2">
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleEdit}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Series
              </Button>
              <Button
                variant="ghost"
                className="w-full text-oro-error hover:text-oro-error"
                onClick={() => setShowDeleteModal(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Series
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Series Delete Modal */}
      <SeriesDeleteModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isDeleting={deleteSeries.isPending}
        seriesTitle={series.title}
        upcomingCount={series.upcoming_count}
      />
    </div>
  );
}
